import { getRenamedData } from 'flying-squid/dist/blockRenames'
import { BlockModel } from 'mc-assets'
import { versionToNumber } from 'mc-assets/dist/utils'
import type { ResourcesManagerCommon } from '../../../src/resourcesManager'

export type ResolvedItemModelRender = {
  modelName: string,
  originalItemName?: string
}

export const renderSlot = (model: ResolvedItemModelRender, resourcesManager: ResourcesManagerCommon, debugIsQuickbar = false, fullBlockModelSupport = false): {
  texture: string,
  blockData: Record<string, { slice, path }> & { resolvedModel: BlockModel } | null,
  scale: number | null,
  slice: number[] | null,
  modelName: string | null,
} => {
  let itemModelName = model.modelName
  const isItem = loadedData.itemsByName[itemModelName]
  const currentResources = resourcesManager.currentResources ?? appViewer.resourcesManager.currentResources
  const runtimeVersion = bot?.version ?? currentResources?.version ?? '1.13'

  // #region normalize item name
  if (versionToNumber(runtimeVersion) < versionToNumber('1.13')) {
    itemModelName = getRenamedData(isItem ? 'items' : 'blocks', itemModelName, runtimeVersion, '1.13.1') as string
  }
  // #endregion


  let itemTexture
  const itemsRenderer = currentResources?.itemsRenderer

  if (!itemsRenderer) {
    return {
      texture: 'blocks',
      blockData: null,
      scale: null,
      slice: [0, 0, 16, 16],
      modelName: itemModelName,
    }
  }

  if (!fullBlockModelSupport) {
    const atlas = resourcesManager.currentResources?.guiAtlas?.json
    // todo atlas holds all rendered blocks, not all possibly rendered item/block models, need to request this on demand instead (this is how vanilla works)
    const tryGetAtlasTexture = (name?: string) => name && atlas?.textures[name.replace('minecraft:', '').replace('block/', '').replace('blocks/', '').replace('item/', '').replace('items/', '').replace('_inventory', '')]
    const item = tryGetAtlasTexture(itemModelName) ?? tryGetAtlasTexture(model.originalItemName)
    if (item) {
      const x = item.u * atlas.width
      const y = item.v * atlas.height
      return {
        texture: 'gui',
        slice: [x, y, atlas.tileSize, atlas.tileSize],
        scale: 0.25,
        blockData: null,
        modelName: null
      }
    }
  }

  const blockToTopTexture = (r) => r?.top ?? r

  try {
    itemTexture =
      itemsRenderer.getItemTexture(itemModelName, {}, false, fullBlockModelSupport)
      ?? (model.originalItemName ? itemsRenderer.getItemTexture(model.originalItemName, {}, false, fullBlockModelSupport) : undefined)
      ?? itemsRenderer.getItemTexture('item/missing_texture')
  } catch (err) {
    // get resourcepack from resource manager
    reportError?.(`Failed to render item ${itemModelName} (original: ${model.originalItemName}) on ${runtimeVersion} (resourcepack: TODO!): ${err.stack}`)
    itemTexture = blockToTopTexture(itemsRenderer.getItemTexture('errored') ?? itemsRenderer.getItemTexture('unknown'))
  }

  itemTexture ??= blockToTopTexture(itemsRenderer.getItemTexture('unknown') ?? itemsRenderer.getItemTexture('item/missing_texture'))

  if (!itemTexture) {
    return {
      texture: 'blocks',
      blockData: null,
      scale: null,
      slice: [0, 0, 16, 16],
      modelName: itemModelName,
    }
  }


  if ('type' in itemTexture) {
    // is item
    return {
      texture: itemTexture.type,
      slice: itemTexture.slice,
      modelName: itemModelName,
      blockData: null,
      scale: null
    }
  } else {
    // is block
    return {
      texture: 'blocks',
      blockData: itemTexture,
      modelName: itemModelName,
      slice: null,
      scale: null
    }
  }
}
